import {
  createElement,
  useRef,
  useEffect,
  useImperativeHandle,
  ComponentRef,
  ElementType,
  RefObject,
} from "react";

import { Modal, ModalConfig } from "lisn.js";
import { WidgetComponentRef, WidgetComponentProps } from "./types";

export type ModalComponentRef = WidgetComponentRef<Modal>;

export type ModalComponentProps<T extends ElementType> = WidgetComponentProps<
  T,
  ModalComponentRef,
  Omit<ModalConfig, "triggers">
> & {
  triggers?: RefObject<Element | null>[];
};

export const ModalComponent = <T extends ElementType = "div">({
  as,
  children,
  triggers,
  config,
  widgetRef,
  ...props
}: ModalComponentProps<T>) => {
  const elementRef = useRef<ComponentRef<T>>(null);
  const widgetRefInternal = useRef<Modal>(null);

  useEffect(() => {
    const triggerElements = triggers
      ?.map((r) => r.current)
      .filter((e) => e !== null);

    if (elementRef.current instanceof HTMLElement) {
      widgetRefInternal.current = new Modal(
        elementRef.current,
        Object.assign(
          {
            triggers: triggerElements,
          },
          config,
        ),
      );
    }

    return () => {
      widgetRefInternal.current?.destroy();
    };
  }, [triggers, config]);

  useImperativeHandle(widgetRef, () => {
    return {
      getWidget: () => widgetRefInternal.current,
    };
  });

  return (
    <>{createElement(as || "div", { ref: elementRef, ...props }, children)}</>
  );
};
